/**
 * Admin - Empresas: todas as empresas com saude, retorno do periodo e a
 * comparacao com o periodo anterior, pronta para centenas de contas.
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade com o total cadastrado e a acao "Nova empresa".
 *  2. Contador (status e saude, cada numero recorta a lista) ao lado dos
 *     totais do periodo com variacao e a projecao de 30 dias no ritmo atual.
 *  3. "O que fazer agora" (acoes derivadas da saude e do retorno de todas as
 *     empresas) ao lado do ranking do periodo.
 *  4. Busca, ordem, vista e filtros combinaveis com contagem facetada.
 *  5. O resultado: tabela paginada (padrao acima de 12 empresas) ou cartoes
 *     com "ver mais". Nunca a lista inteira de uma vez.
 *  6. Rodape com o periodo e a base da comparacao.
 *
 * Decisoes nao obvias:
 *  - A LISTA vem de `useCompany().companies` (a mesma fonte do seletor do
 *    cabecalho); a rota `/api/admin/companies` so ENRIQUECE cada linha com o
 *    retorno e a prontidao. Se a rota falhar, o cadastro continua na tela com
 *    os numeros em "—" e a saude "Sem dados": a equipe ainda abre o CRM.
 *  - A consulta (busca, filtros, ordem, vista) mora na URL com prefixo `emp.`
 *    (lista.ts): o "voltar" do navegador e o "voltar" do detalhe devolvem a
 *    lista no mesmo recorte, e o link pode ser compartilhado.
 *  - A saude e derivada no navegador (saude.ts) dos fatos que a rota ja manda:
 *    nenhuma requisicao por empresa, o que importa com centenas delas.
 *  - "Abrir CRM" troca a empresa em foco e leva a visao geral preservando o
 *    periodo da URL, mas SEM a consulta da lista (que nao e da visao geral).
 *  - Toda leitura parte de um fato ja ocorrido (checkout abandonado ha 30
 *    min, assinatura ja vencida). Nao existe bloco de "vai vencer".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, HeroBanner, LinkButton } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import type { CompanyStatus } from '../../../types';
import { fetchCompanies } from '../api';
import { diasEntre } from '../comparar';
import { CartaoEmpresa } from '../components/CartaoEmpresa';
import { FiltrosEmpresas } from '../components/FiltrosEmpresas';
import { OQueFazerAgora } from '../components/OQueFazerAgora';
import { RankingEmpresas, type MedidaRanking } from '../components/RankingEmpresas';
import { TabelaEmpresas } from '../components/TabelaEmpresas';
import { ContadorDeEmpresas, TotaisDoPeriodo } from '../components/TopoDaLista';
import { gerarInsightsDaLista, type AcaoInsight } from '../insights';
import {
  CONSULTA_PADRAO,
  LOTE_DE_CARTOES,
  TAMANHO_DA_PAGINA,
  alternarOrdem,
  escreverConsulta,
  filtrar,
  filtrosAtivos,
  lerConsulta,
  ordenar,
  semConsulta,
  vistaEfetiva,
  type LinhaEmpresa,
} from '../lista';
import { agoraDaTela, diagnosticar } from '../saude';
import type { AbaDetalhe, ColunaLista, CompanyOverviewItem, ConsultaLista, NivelSaude } from '../types';
import { STATUS_LISTA } from '../vocabulario';

const PARAM_ABA = 'emp.aba';

function EsqueletoDoTopo(): JSX.Element {
  return (
    <>
      <p role="status" className="sr-only">
        Carregando o retorno das empresas
      </p>
      <div aria-hidden="true" className="flex flex-col gap-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="h-56 animate-pulse rounded-2xl border border-line bg-surface" />
          <div className="h-56 animate-pulse rounded-2xl border border-line bg-surface lg:col-span-2" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="h-64 animate-pulse rounded-2xl border border-line bg-surface lg:col-span-2" />
          <div className="h-64 animate-pulse rounded-2xl border border-line bg-surface" />
        </div>
        <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
        <div className="h-96 animate-pulse rounded-2xl border border-line bg-surface" />
      </div>
    </>
  );
}

function rolarAte(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function AdminEmpresas(): JSX.Element {
  const { companies, selectCompany } = useCompany();
  const { range, selection } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [limiteCartoes, setLimiteCartoes] = useState(LOTE_DE_CARTOES);
  const [medida, setMedida] = useState<MedidaRanking>('recuperado');

  useEffect(() => {
    document.title = 'Empresas — Administração · Converte Fácil';
  }, []);

  const consulta = useMemo(() => lerConsulta(location.search), [location.search]);

  const buscar = useCallback((signal: AbortSignal) => fetchCompanies(range, signal), [range]);
  const { state, reload } = useApiResource(buscar, 60_000);
  const dados = state.status === 'success' ? state.data : null;

  const itensPorId = useMemo<ReadonlyMap<string, CompanyOverviewItem>>(() => new Map((dados?.items ?? []).map((item) => [item.companyId, item])), [dados]);

  const linhas = useMemo<readonly LinhaEmpresa[]>(() => {
    const agora = agoraDaTela();
    return companies.map((company) => {
      const item = itensPorId.get(company.id) ?? null;
      return {
        company,
        item,
        saude: item === null ? null : diagnosticar(company, item.setup, item.metrics, item.previous, agora),
        provedores: item === null ? company.providers : item.setup.connectedProviders,
      };
    });
  }, [companies, itensPorId]);

  const recorte = useMemo(() => ordenar(filtrar(linhas, consulta), consulta.ordem), [linhas, consulta]);
  const insights = useMemo(() => (dados === null ? [] : gerarInsightsDaLista(linhas, agoraDaTela())), [dados, linhas]);

  const vista = vistaEfetiva(consulta, companies.length);
  const ultimaPagina = Math.max(0, Math.floor((recorte.length - 1) / TAMANHO_DA_PAGINA) * TAMANHO_DA_PAGINA);
  const offsetSeguro = Math.min(offset, ultimaPagina);
  const pagina = recorte.slice(offsetSeguro, offsetSeguro + TAMANHO_DA_PAGINA);
  const cartoes = recorte.slice(0, limiteCartoes);

  const atualizar = useCallback(
    (patch: Partial<ConsultaLista>): void => {
      setSearchParams((anteriores) => escreverConsulta(anteriores.toString(), { ...lerConsulta(anteriores.toString()), ...patch }), { replace: true });
      setOffset(0);
      setLimiteCartoes(LOTE_DE_CARTOES);
    },
    [setSearchParams],
  );

  const recortarERolar = useCallback(
    (patch: Partial<ConsultaLista>): void => {
      atualizar(patch);
      rolarAte('lista-empresas');
    },
    [atualizar],
  );

  const limparTudo = (): void => atualizar({ ...CONSULTA_PADRAO, ordem: consulta.ordem, vista: consulta.vista });

  const linkDe = useCallback(
    (companyId: string, aba?: AbaDetalhe): string => {
      const p = new URLSearchParams(location.search);
      if (aba === undefined || aba === 'resumo') {
        p.delete(PARAM_ABA);
      } else {
        p.set(PARAM_ABA, aba);
      }
      const texto = p.toString();
      return `/admin/empresas/${encodeURIComponent(companyId)}${texto === '' ? '' : `?${texto}`}`;
    },
    [location.search],
  );

  const abrirCrm = useCallback(
    (companyId: string): void => {
      selectCompany(companyId);
      navigate({ pathname: '/', search: semConsulta(location.search) });
    },
    [selectCompany, navigate, location.search],
  );

  const executar = (acao: AcaoInsight): void => {
    if (acao.tipo === 'filtro') {
      recortarERolar(acao.consulta);
      return;
    }
    navigate(linkDe(acao.companyId, acao.aba));
  };

  const ordenarPor = (coluna: ColunaLista): void => atualizar({ ordem: alternarOrdem(consulta.ordem, coluna) });

  const porStatus = useMemo<Readonly<Record<CompanyStatus, number>>>(() => {
    const base: Record<CompanyStatus, number> = { ativa: 0, onboarding: 0, pausada: 0 };
    for (const c of companies) {
      base[c.status] += 1;
    }
    return base;
  }, [companies]);

  const porSaude = useMemo<Readonly<Record<NivelSaude, number>>>(() => {
    const base: Record<NivelSaude, number> = { saudavel: 0, atencao: 0, critica: 0, implantacao: 0, pausada: 0 };
    for (const l of linhas) {
      if (l.saude !== null) {
        base[l.saude.nivel] += 1;
      }
    }
    return base;
  }, [linhas]);

  const novas = companies.filter((c) => (range.from === null || c.createdAt >= range.from) && (range.to === null || c.createdAt < range.to)).length;
  const periodoTexto = describeSelection(selection);
  const ativos = filtrosAtivos(consulta);
  const linkNova = `/admin/empresas/nova${semConsulta(location.search)}`;

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="empresa"
        title="Empresas"
        badges={
          <>
            <Badge tone="neutral">{formatCount(companies.length)} cadastradas</Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description={
          <>
            Saúde, retorno e acesso ao CRM de cada empresa · <strong className="text-ink">{periodoTexto}</strong>. Todo número nasce de um checkout já abandonado ou de uma
            assinatura já vencida.
          </>
        }
        aside={
          <>
            <RefreshingDot active={state.status === 'success' && state.refreshing} />
            <LinkButton to={linkNova} variant="primary" icon="mais" keepSearch={false}>
              Nova empresa
            </LinkButton>
          </>
        }
      />

      {state.status === 'loading' ? <EsqueletoDoTopo /> : null}

      {state.status === 'error' ? (
        <ErrorPanel title="Não foi possível carregar o retorno e a saúde das empresas. O cadastro continua abaixo." error={state.error} onRetry={reload} />
      ) : null}

      {state.status === 'success' && state.simulated ? (
        <SimulatedNotice>Empresas, ofertas, integrações e números vêm do catálogo fictício da prévia. Nada aqui é uma conta real.</SimulatedNotice>
      ) : null}

      {dados !== null ? (
        <>
          <div className="grid gap-6 lg:grid-cols-3">
            <ContadorDeEmpresas porStatus={porStatus} porSaude={porSaude} novas={novas} total={companies.length} onFiltrar={recortarERolar} />
            <TotaisDoPeriodo totals={dados.totals} anterior={dados.previousTotals} dias={diasEntre(dados.periodStart, dados.periodEnd)} periodo={periodoTexto} className="lg:col-span-2" />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgora insights={insights} onAcao={executar} className="lg:col-span-2" />
            <RankingEmpresas
              linhas={linhas}
              medida={medida}
              onMedida={setMedida}
              linkDe={linkDe}
              onVerTodas={(coluna) => recortarERolar({ ordem: { coluna, desc: true } })}
            />
          </div>
        </>
      ) : null}

      {state.status === 'loading' ? null : (
        <section id="lista-empresas" aria-label="Lista de empresas" className="flex scroll-mt-4 flex-col gap-4">
          <FiltrosEmpresas consulta={consulta} onConsulta={atualizar} linhas={linhas} exibidas={recorte.length} vista={vista} />

          {recorte.length === 0 ? (
            companies.length === 0 ? (
              <EmptyState
                title="Nenhuma empresa cadastrada ainda"
                description="Cada empresa conectada começa a render comissão no primeiro checkout abandonado que a régua recupera. O assistente tem sete etapas e já entrega as URLs de webhook prontas para colar."
                action={
                  <LinkButton to={linkNova} variant="primary" icon="mais" keepSearch={false}>
                    Cadastrar a primeira empresa
                  </LinkButton>
                }
              />
            ) : (
              <EmptyState
                title="Nenhuma empresa neste recorte"
                description={
                  <>
                    Nenhuma das {formatCount(companies.length)} empresas combina com {ativos.map((f) => f.rotulo).join(' + ')}. A busca procura em nome, segmento, contato,
                    provedor e saúde; tire um filtro de cada vez pelas pastilhas acima para ver onde o recorte zera.
                  </>
                }
                action={
                  <Button variant="secondary" icon="x" onClick={limparTudo}>
                    Limpar filtros e busca
                  </Button>
                }
              />
            )
          ) : vista === 'tabela' ? (
            <TabelaEmpresas
              linhas={pagina}
              recorte={recorte}
              ordem={consulta.ordem}
              onOrdenar={ordenarPor}
              offset={offsetSeguro}
              onOffset={(novo) => {
                setOffset(novo);
                rolarAte('lista-empresas');
              }}
              tamanhoPagina={TAMANHO_DA_PAGINA}
              linkDe={linkDe}
              onAbrirCrm={abrirCrm}
            />
          ) : (
            <>
              <ul aria-label="Empresas em cartões" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {cartoes.map((linha) => (
                  <CartaoEmpresa key={linha.company.id} linha={linha} onAbrirCrm={abrirCrm} linkDe={linkDe} />
                ))}
              </ul>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-ink-subtle">
                  Mostrando {formatCount(cartoes.length)} de {formatCount(recorte.length)} · ordem: {ordemLegivel(consulta)}
                </p>
                {cartoes.length < recorte.length ? (
                  <Button variant="secondary" icon="mais" onClick={() => setLimiteCartoes((n) => n + LOTE_DE_CARTOES)}>
                    Ver mais {formatCount(Math.min(LOTE_DE_CARTOES, recorte.length - cartoes.length))} de {formatCount(recorte.length - cartoes.length)} restantes
                  </Button>
                ) : null}
              </div>
            </>
          )}
        </section>
      )}

      {dados !== null ? (
        <p className="text-xs text-ink-subtle">
          Período de {formatDate(dados.periodStart)} a {formatDate(dados.periodEnd)} ({periodoTexto})
          {dados.previousPeriod === null
            ? ' · sem período anterior para comparar (desde o início)'
            : ` · comparado com ${formatDate(dados.previousPeriod.start)} a ${formatDate(dados.previousPeriod.end)}, o período anterior de mesmo tamanho`}
          {' · '}
          {STATUS_LISTA.map((s) => `${formatCount(porStatus[s])} ${s === 'ativa' ? 'ativas' : s === 'onboarding' ? 'em implantação' : 'pausadas'}`).join(', ')}.
        </p>
      ) : null}
    </section>
  );
}

function ordemLegivel(consulta: ConsultaLista): string {
  const nomes: Readonly<Record<ColunaLista, string>> = {
    empresa: 'nome',
    saude: 'saúde',
    recuperado: 'recuperado',
    variacao: 'variação',
    comissao: 'comissão',
    taxa: 'taxa',
    vendas: 'vendas',
    criada: 'data de cadastro',
  };
  return `${nomes[consulta.ordem.coluna]} (${consulta.ordem.desc ? 'decrescente' : 'crescente'})`;
}
